import { useEffect, useRef, useState } from 'react';
import { Button, Input } from '@hrms/ui';
import {
  createPhoneVerifier,
  maskPhone,
  PhoneVerificationError,
  phoneVerificationMessage,
  type PhoneAutoHandlers,
  type PhoneCodeSession,
  type PhoneVerificationConfig,
  type PhoneVerifier,
} from '../../lib/phoneVerification.js';
import { DEFAULT_DIAL_CODE, splitInternational } from '../../routes/auth/signupPhone.js';

const RESEND_AFTER_SECONDS = 30;

function messageOf(error: unknown): string {
  return error instanceof PhoneVerificationError
    ? error.message
    : phoneVerificationMessage('unknown');
}

/** "+91 98765 •••10" for the number the SMS goes to. */
export function maskedPinPhone(phone: string): string {
  const { dialCode } = splitInternational(DEFAULT_DIAL_CODE, phone);
  return maskPhone(phone, `+${dialCode}`);
}

/**
 * "Forgot PIN", step one: prove the person's own mobile with an SMS code — the same Firebase proof
 * as "Forgot password" (`SignupPhoneStep`, ADR 0036), sized for a dialog instead of a page. The number is
 * the one on file (`status.reset.phone`), or — for someone with none — the number they add after confirming
 * their password (`SalaryPinNewPhoneFlow`).
 *
 * `createPhoneVerifier` picks the phone's own Firebase SDK in the Android app (Play Integrity, SMS
 * auto-read) and the web SDK with an invisible reCAPTCHA in the browser.
 */
export function SalaryPinPhoneCode({
  config,
  phone,
  onVerified,
}: {
  config: PhoneVerificationConfig;
  /** E.164, from the status. */
  phone: string;
  onVerified: (proof: string) => void;
}) {
  const recaptchaRef = useRef<HTMLDivElement>(null);
  const verifierRef = useRef<PhoneVerifier | null>(null);
  const sessionRef = useRef<PhoneCodeSession | null>(null);
  const [stage, setStage] = useState<'sending' | 'sent' | 'send_failed' | 'confirming'>('sending');
  const [code, setCode] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [resendIn, setResendIn] = useState(0);
  const autoCodeRef = useRef<string | null>(null);
  const confirmRef = useRef<(value: string) => Promise<void>>(async () => undefined);
  const onVerifiedRef = useRef(onVerified);

  const autoHandlers: PhoneAutoHandlers = {
    onCode: (value) => {
      setCode(value);
      if (sessionRef.current) void confirmRef.current(value);
      else autoCodeRef.current = value;
    },
    onProof: (proof) => {
      setProblem(null);
      onVerifiedRef.current(proof);
    },
    onFailure: (error) => setProblem(messageOf(error)),
  };

  async function send() {
    setProblem(null);
    setCode('');
    autoCodeRef.current = null;
    setStage('sending');
    try {
      verifierRef.current ??= await createPhoneVerifier(config, recaptchaRef.current!);
      sessionRef.current = await verifierRef.current.send(phone, autoHandlers);
      setStage('sent');
      setResendIn(RESEND_AFTER_SECONDS);
      const early = autoCodeRef.current;
      autoCodeRef.current = null;
      if (early) void confirmRef.current(early);
    } catch (error) {
      setProblem(messageOf(error));
      setStage('send_failed');
    }
  }

  async function confirm(value: string) {
    if (!sessionRef.current || stage === 'confirming') return;
    setProblem(null);
    setStage('confirming');
    try {
      const proof = await sessionRef.current.confirm(value);
      onVerified(proof);
      setStage('sent');
    } catch (error) {
      setProblem(messageOf(error));
      setCode('');
      setStage('sent');
    }
  }

  useEffect(() => {
    confirmRef.current = confirm;
    onVerifiedRef.current = onVerified;
  });

  // Once on arrival; a ref so StrictMode's double effect does not text twice.
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void send();
  }, []);

  useEffect(() => () => verifierRef.current?.dispose(), []);

  useEffect(() => {
    if (resendIn <= 0) return;
    const timer = window.setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [resendIn]);

  const masked = maskedPinPhone(phone);

  return (
    <form
      className="flex flex-col gap-4"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        if (code.length === 6) void confirm(code);
        else setProblem('Enter the 6-digit code from the SMS.');
      }}
    >
      <p className="text-sm text-fg-muted">
        {stage === 'sending' ? (
          <>
            Sending a code to <span className="font-semibold text-fg">{masked}</span>…
          </>
        ) : stage === 'send_failed' ? (
          <>
            We could not send a code to <span className="font-semibold text-fg">{masked}</span>.
          </>
        ) : (
          <>
            Enter the 6-digit code we sent to{' '}
            <span className="font-semibold text-fg">{masked}</span>.
          </>
        )}
      </p>
      <Input
        label="Verification code"
        name="code"
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern="[0-9]{6}"
        maxLength={6}
        placeholder="000000"
        className="text-center font-mono text-lg tracking-[0.5em]"
        value={code}
        disabled={stage === 'sending' || stage === 'confirming'}
        error={problem ?? undefined}
        onChange={(event) => {
          const digits = event.target.value.replace(/\D/g, '').slice(0, 6);
          setCode(digits);
          setProblem(null);
          if (digits.length === 6) void confirm(digits);
        }}
      />
      <div className="flex flex-wrap items-center justify-between gap-3">
        {stage === 'send_failed' ? (
          <Button type="button" variant="ghost" size="sm" onClick={() => void send()}>
            Try again
          </Button>
        ) : resendIn > 0 || stage === 'sending' ? (
          <span className="text-sm text-fg-muted" aria-live="polite">
            Resend in {resendIn}s
          </span>
        ) : (
          <Button type="button" variant="ghost" size="sm" onClick={() => void send()}>
            Resend code
          </Button>
        )}
        <Button
          type="submit"
          variant="primary"
          loading={stage === 'confirming'}
          disabled={stage === 'sending' || stage === 'send_failed'}
        >
          Verify
        </Button>
      </div>
      {/* Firebase's invisible reCAPTCHA renders here; it must stay mounted while codes are sent. */}
      <div ref={recaptchaRef} />
    </form>
  );
}
